// Wave AW.3-React (2026-05-16) — 외부 바이어 대시보드.
// Wave UX-2.1 Item 2 (2026-05-23) — placeholder → 실 위젯 3종.
//   1. _SpeedCard — /trust-speed/me (신뢰속도 + 등급)
//   2. _CountRow — /me/bids + /me/favorites (counts)
//   3. _RecommendedSection — /auction/list?sort=ending_soon&limit=3
// Promise.allSettled — 4 fetch 병렬, 부분 실패해도 화면 진입 보존.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import AuctionBuyerCard from './AuctionBuyerCard';
import OnboardingModal from './OnboardingModal';
import { useT } from '../../i18n/I18nProvider';
import { getGrade, gradeI18nKey } from '../../utils/trustSpeedGrades';

// 신뢰속도 카드 — 당근마켓 매너온도(°C) 대체. 등급 + 속도(km/h) + 매입 완료 건수.
function SpeedCard(props) {
  var t = props.t;
  var s = props.speed;
  var grade = (s && s.grade) || 'IDLE';
  var g = getGrade(grade);
  var speedVal = s ? Math.round(Number(s.trustSpeed) || 0) : 0;
  var completed = s ? (s.completedDeals || 0) : 0;
  return (
    <div style={{
      background: 'linear-gradient(135deg, ' + g.color + ' 0%, #1B3A5C 100%)',
      color: 'white', borderRadius: 16,
      padding: '24px 28px', boxShadow: '0 4px 20px rgba(27,58,92,0.18)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.85, letterSpacing: '0.04em' }}>
          {t('speedCardTitle')}
        </div>
        <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4 }}>{t('speedCardTapHint')}</div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 10,
          background: 'rgba(255,255,255,0.18)', padding: '4px 12px', borderRadius: 999,
          fontSize: 12, fontWeight: 800,
        }}>
          <span style={{ fontSize: 15 }}>{g.emoji}</span>{t(gradeI18nKey(grade))}
          <span style={{ opacity: 0.7, fontWeight: 600 }}>· {t('purchaseCompletedLabel')} {completed}</span>
        </div>
      </div>
      <div style={{ textAlign: 'right' }} dir="ltr">
        <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1, color: '#ffffff', textShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
          {speedVal}<span style={{ fontSize: 18, fontWeight: 700, marginLeft: 3 }}>{t('speedCardUnit')}</span>
        </div>
      </div>
    </div>
  );
}

function CountCard(props) {
  var label = props.label;
  var count = props.count;
  var icon = props.icon;
  var onClick = props.onClick;
  return (
    <div onClick={onClick}
      className={onClick ? 'buyer-card-lift' : undefined}
      style={{
        flex: 1, background: 'white',
        border: '1px solid #e2e8f0', borderRadius: 12,
        padding: '18px 16px', cursor: onClick ? 'pointer' : 'default',
        }}
      >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 18, opacity: 0.7 }}>{icon}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>{label}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>{count}</div>
    </div>
  );
}

function AuctionBuyerDashboard() {
  var t = useT();
  var navigate = useNavigate();
  // perf/buyer-iife-lazy (2026-05-24) — useState lazy init (mount 1회) → 매 render IIFE 차단
  var [buyer] = useState(function(){
    try { return JSON.parse(sessionStorage.getItem('autodex_buyer')||'null'); } catch(e) { return null; }
  });
  var displayName = (buyer && buyer.name) ? buyer.name : t('dashboardFallbackName');

  var [speed, setSpeed] = useState(null);
  var [activeBids, setActiveBids] = useState(0);
  var [favoritesCount, setFavoritesCount] = useState(0);
  var [wonCount, setWonCount] = useState(0);
  var [recommended, setRecommended] = useState([]);
  var [loaded, setLoaded] = useState(false);
  // Wave DW-1 (2026-05-24) — 거래 완료 확인 대기 (TW-1 활용)
  var [txPending, setTxPending] = useState({ count: 0, reminded: 0 });
  // Wave DW-2 (2026-05-24) — 진행 중 분쟁
  var [disputesCount, setDisputesCount] = useState(0);
  // Wave DW-3 (2026-05-24) — 진행 중 RFQ
  var [rfqActiveCount, setRfqActiveCount] = useState(0);
  // Wave DW-4 (2026-05-24) — 최근 활동 피드 (최대 5건)
  var [activity, setActivity] = useState([]);

  var load = useCallback(function() {
    // 4 fetch 병렬 — 실패해도 화면 진입 보존.
    var hitAuth = false;
    function checkAuth(r) {
      if (isUnauthorized(r)) { hitAuth = true; throw new Error('unauthorized'); }
      return r.json();
    }

    Promise.allSettled([
      authBuyerFetch(API + '/buyer-app/trust-speed/me', {}).then(checkAuth),
      authBuyerFetch(API + '/buyer-app/me/bids', {}).then(checkAuth),
      authBuyerFetch(API + '/buyer-app/me/favorites', {}).then(checkAuth),
      authBuyerFetch(API + '/buyer-app/auction/list?status=active&sort=ending_soon&limit=3', {}).then(checkAuth),
      // Wave DW-1 — 거래 완료 확인 대기 카운트
      authBuyerFetch(API + '/buyer-app/transactions/pending', {}).then(checkAuth),
      // Wave DW-2 — 진행 중 분쟁 카운트
      authBuyerFetch(API + '/buyer-app/transactions/my-disputes', {}).then(checkAuth),
      // Wave DW-3 — 진행 중 RFQ 카운트
      authBuyerFetch(API + '/buyer-app/rfq/me/active', {}).then(checkAuth),
      // Wave DW-4 — 최근 활동 피드
      authBuyerFetch(API + '/buyer-app/me/activity?limit=5', {}).then(checkAuth),
    ]).then(function(results) {
      if (hitAuth) { handleUnauthorized(navigate); return; }
      var speedRes = results[0].status === 'fulfilled' ? results[0].value : null;
      var bidsRes = results[1].status === 'fulfilled' ? results[1].value : null;
      var favRes = results[2].status === 'fulfilled' ? results[2].value : null;
      var listRes = results[3].status === 'fulfilled' ? results[3].value : null;
      var txRes = results[4].status === 'fulfilled' ? results[4].value : null;

      if (speedRes && speedRes.success && speedRes.data) setSpeed(speedRes.data);

      if (bidsRes && bidsRes.success && bidsRes.data && Array.isArray(bidsRes.data.bids)) {
        var active = bidsRes.data.bids.filter(function(a){ return a && a.myBid && a.myBid.status === 'active'; }).length;
        var won = bidsRes.data.bids.filter(function(a){ return a && a.myBid && a.myBid.status === 'winner'; }).length;
        setActiveBids(active);
        setWonCount(won);
      }

      if (favRes && favRes.success && favRes.data && Array.isArray(favRes.data.auctions)) {
        setFavoritesCount(favRes.data.auctions.length);
      }

      if (listRes && listRes.success && listRes.data && Array.isArray(listRes.data.auctions)) {
        setRecommended(listRes.data.auctions.slice(0, 3));
      }

      // Wave DW-1 — txPending: count + reminded
      if (txRes && txRes.success && txRes.data && Array.isArray(txRes.data.items)) {
        var items = txRes.data.items;
        var reminded = items.filter(function(it){ return (it.reminder_count || 0) > 0; }).length;
        setTxPending({ count: items.length, reminded: reminded });
      }

      // Wave DW-2 — 진행 중 분쟁
      var dispRes = results[5].status === 'fulfilled' ? results[5].value : null;
      if (dispRes && dispRes.success && dispRes.data && Array.isArray(dispRes.data.items)) {
        setDisputesCount(dispRes.data.items.length);
      }

      // Wave DW-3 — 진행 중 RFQ
      var rfqRes = results[6].status === 'fulfilled' ? results[6].value : null;
      if (rfqRes && rfqRes.success && rfqRes.data && typeof rfqRes.data.count === 'number') {
        setRfqActiveCount(rfqRes.data.count);
      }

      // Wave DW-4 — 최근 활동 피드
      var actRes = results[7].status === 'fulfilled' ? results[7].value : null;
      if (actRes && actRes.success && actRes.data && Array.isArray(actRes.data.items)) {
        setActivity(actRes.data.items);
      }

      setLoaded(true);
    });
  }, [navigate]);

  useEffect(function() { load(); }, [load]);

  return (
    <AuctionBuyerLayout activeKey={null}>
      {/* Wave UX-2.2 #9 — 첫 로그인 온보딩 (localStorage flag 로 1회만) */}
      <OnboardingModal />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 24px 80px', }}>
        {/* 인사말 */}
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
            {t('dashboardGreeting', { name: displayName })}
          </h2>
          <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>{t('dashboardSubtitle')}</p>
        </div>

        {/* 신뢰속도 카드 */}
        <SpeedCard t={t} speed={speed} />

        {/* Wave DW-1 — 거래 완료 확인 대기 위젯 (count > 0 시만 노출) */}
        {txPending.count > 0 && (
          <div onClick={function(){ navigate('/buyer/transactions'); }}
            style={{
              marginTop: 16, padding: '14px 18px',
              background: 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)',
              color: 'white', borderRadius: 12, cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              boxShadow: '0 2px 8px rgba(27,58,92,0.18)',
            }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{t('dashboardTxPendingTitle')}</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 3 }}>
                {t('dashboardTxPendingCount', { n: txPending.count })}
                {txPending.reminded > 0 && ' · ' + t('dashboardTxPendingReminded', { m: txPending.reminded })}
              </div>
            </div>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{t('dashboardTxPendingCta')}</span>
          </div>
        )}

        {/* Wave DW-2 — 진행 중 분쟁 위젯 (count > 0 시만 노출, 빨간 톤) */}
        {disputesCount > 0 && (
          <div onClick={function(){ navigate('/buyer/transactions'); }}
            style={{
              marginTop: 12, padding: '14px 18px',
              background: 'linear-gradient(135deg, #991b1b 0%, #dc2626 100%)',
              color: 'white', borderRadius: 12, cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              boxShadow: '0 2px 8px rgba(153,27,27,0.18)',
            }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{t('dashboardDisputesTitle')}</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 3 }}>
                {t('dashboardDisputesCount', { n: disputesCount })}
              </div>
            </div>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{t('dashboardDisputesCta')}</span>
          </div>
        )}

        {/* Wave DW-3 — 진행 중 RFQ 위젯 (count > 0 시만 노출, 황색 톤) */}
        {rfqActiveCount > 0 && (
          <div onClick={function(){ navigate('/buyer/promise'); }}
            style={{
              marginTop: 12, padding: '14px 18px',
              background: 'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
              color: 'white', borderRadius: 12, cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              boxShadow: '0 2px 8px rgba(180,83,9,0.18)',
            }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{t('dashboardRfqTitle')}</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 3 }}>
                {t('dashboardRfqCount', { n: rfqActiveCount })}
              </div>
            </div>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{t('dashboardRfqCta')}</span>
          </div>
        )}

        {/* 카운트 행 (4 카드 — DW-1: 거래 대기 추가) */}
        <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          <CountCard label={t('countCardActiveBids')} count={activeBids} icon="🎯"
            onClick={function(){ navigate('/buyer/bids'); }} />
          <CountCard label={t('countCardFavorites')} count={favoritesCount} icon="⭐"
            onClick={function(){ navigate('/buyer/favorites'); }} />
          <CountCard label={t('countCardWon')} count={wonCount} icon="🏆"
            onClick={function(){ navigate('/buyer/bids'); }} />
          <CountCard label={t('countCardTxPending')} count={txPending.count} icon="📋"
            onClick={function(){ navigate('/buyer/transactions'); }} />
        </div>

        {/* Wave DW-4 — 최근 활동 피드 (5건, 추천 섹션 위) */}
        {loaded && activity.length > 0 && (
          <div style={{ marginTop: 32 }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>
              {t('dashboardActivityTitle')}
            </h3>
            <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: '8px 0' }}>
              {activity.map(function (item, i) {
                var labelKey = {
                  'auction-bid': 'activityTypeAuctionBid',
                  'auction-won': 'activityTypeAuctionWon',
                  'rfq-quote': 'activityTypeRfqQuote',
                  'rfq-committed': 'activityTypeRfqCommitted',
                  'tx-completed': 'activityTypeTxCompleted',
                  'tx-disputed': 'activityTypeTxDisputed',
                  'tx-dispute-resolved': 'activityTypeTxDisputeResolved',
                }[item.type] || 'activityTypeAuctionBid';
                var ts = (item.ts || '').slice(0, 16);
                return (
                  <div key={i}
                    style={{
                      padding: '10px 16px',
                      borderBottom: i < activity.length - 1 ? '1px solid #f1f5f9' : 'none',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                    }}>
                    <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600 }}>
                      {t(labelKey)}
                      {item.amount ? ' · ' + Number(item.amount).toLocaleString() + '원' : ''}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{ts}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 추천 섹션 */}
        <div style={{ marginTop: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{t('recommendedTitle')}</h3>
            <button type="button" onClick={function(){ navigate('/buyer/auctions'); }}
              style={{
                background: 'none', border: 'none', color: '#3b82f6',
                fontSize: 13, fontWeight: 700, cursor: 'pointer',              }}>
              {t('recommendedSeeAll')} →
            </button>
          </div>

          {loaded && recommended.length === 0 ? (
            <div style={{ background: 'white', border: '1px dashed #e2e8f0', borderRadius: 12, padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
              {t('recommendedEmpty')}
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 16,
            }}>
              {recommended.map(function(a) {
                return (
                  <AuctionBuyerCard key={a.id} auction={a}
                    onClick={function(){ navigate('/buyer/auctions/' + a.id); }}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerDashboard;
